import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const CartContext = createContext();

const API = '/api';

function getSessionId() {
  if (typeof window === 'undefined') return null;
  let stored = localStorage.getItem('blc_session_id');
  if (stored) return stored;
  const newId = 'blc_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
  localStorage.setItem('blc_session_id', newId);
  return newId;
}

export function CartProvider({ children }) {
  const sessionId = useMemo(() => getSessionId(), []);
  
  const [cart, setCart] = useState({
    id: null, items: [], subtotal: 0,
    applied_gift_cards: [], applied_points: null, applied_promo: null,
    gift_card_discount: 0, points_discount: 0, promo_discount: 0
  });
  const [loading, setLoading] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await axios.get(`${API}/cart/${sessionId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCart({
        id: null, items: [], subtotal: 0,
        applied_gift_cards: [], applied_points: null, applied_promo: null,
        gift_card_discount: 0, points_discount: 0, promo_discount: 0
      });
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId && !initialized) {
      fetchCart();
      setInitialized(true);
    }
  }, [sessionId, initialized, fetchCart]);

  const addToCart = useCallback(async (productId, quantity = 1) => {
    if (!sessionId || !productId) return;
    setLoading(true);
    try {
      await axios.post(`${API}/cart/${sessionId}/add`, { product_id: productId, quantity });
      await fetchCart();
      setIsCartOpen(true);
    } catch (error) {
      console.error('Error adding to cart:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId, fetchCart]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    if (!sessionId) return;
    if (quantity <= 0) {
      return removeFromCart(productId);
    }
    setLoading(true);
    try {
      await axios.put(`${API}/cart/${sessionId}/update`, { product_id: productId, quantity });
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId, fetchCart]);

  const removeFromCart = useCallback(async (productId) => {
    if (!sessionId) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/cart/${sessionId}/item/${productId}`);
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId, fetchCart]);

  const clearCart = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      for (const item of cart.items) {
        await axios.delete(`${API}/cart/${sessionId}/item/${item.product_id}`);
      }
      await fetchCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
    } finally {
      setLoading(false);
    }
  }, [sessionId, cart.items, fetchCart]);

  // Promo code operations
  const applyPromo = useCallback(async (code) => {
    if (!sessionId || !code) return { success: false, error: 'Code manquant' };
    try {
      const res = await axios.post(`${API}/promo/apply-to-cart`, { code, session_id: sessionId });
      await fetchCart();
      return { success: true, promo: res.data.promo };
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de l\'application du code promo';
      return { success: false, error: msg };
    }
  }, [sessionId, fetchCart]);

  const removePromo = useCallback(async () => {
    if (!sessionId) return;
    try {
      await axios.delete(`${API}/promo/remove-from-cart/${sessionId}`);
      await fetchCart();
    } catch (error) {
      console.error('Error removing promo:', error);
    }
  }, [sessionId, fetchCart]);

  const cartItemsCount = useMemo(() =>
    cart.items.reduce((sum, item) => sum + item.quantity, 0),
    [cart.items]
  );

  const refreshCart = useCallback(() => { fetchCart(); }, [fetchCart]);

  const value = useMemo(() => ({
    cart, loading, isCartOpen, setIsCartOpen,
    addToCart, updateQuantity, removeFromCart, clearCart,
    fetchCart, refreshCart, cartItemsCount, sessionId,
    applyPromo, removePromo,
  }), [cart, loading, isCartOpen, addToCart, updateQuantity, removeFromCart, clearCart, fetchCart, refreshCart, cartItemsCount, sessionId, applyPromo, removePromo]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
}
